import React, { useState } from 'react';
import {
    loadStripe,
    StripeElementsOptions
} from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { C, Card, Btn, Alert } from './UI';
import { useBranding } from '../context/BrandingContext';

// Stripe.js is initialized with the publishable key served by the API (GET /api/tenant/branding),
// which comes from the same config source as the server's secret key — so the two can never drift
// between test and live. loadStripe must be called once per key; memoize across re-renders. The
// build-time env var remains only as a local-dev fallback.
let _stripeKey = '';
let _stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripePromise(key: string | null | undefined) {
    const k = key || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    if (!_stripePromise || _stripeKey !== k) {
        _stripeKey = k;
        _stripePromise = loadStripe(k);
    }
    return _stripePromise;
}

export function isTestStripeKey(key: string | null | undefined) {
    return (key || '').startsWith('pk_test');
}

// ── Checkout form ─────────────────────────────────────────────────────────────

interface CheckoutFormProps {
    amount: number;
    onSuccess: () => void;
    onCancel: () => void;
}

function CheckoutForm({ amount, onSuccess, onCancel }: CheckoutFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const testMode = isTestStripeKey(useBranding().stripePublishableKey);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [ready, setReady] = useState(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) return;
        setError('');
        setLoading(true);

        // Confirm the payment — Stripe handles all payment methods automatically
        const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',   // prevents redirect for card payments
            confirmParams: {
                return_url: window.location.href,  // fallback for redirect-based methods
            }
        });

        if (stripeError) {
            setError(stripeError.message || 'Payment failed');
            setLoading(false);
            return;
        }

        if (paymentIntent?.status === 'requires_capture' ||
            paymentIntent?.status === 'succeeded') {
            onSuccess();
        }

        setLoading(false);
    };

    return (
        <div>
            <Alert message={error} />

            {/* PaymentElement shows card, Apple Pay, Google Pay, Link etc automatically */}
            <div style={{ marginBottom: 20 }}>
                <PaymentElement
                    onReady={() => setReady(true)}
                    options={{
                        layout: 'tabs',   // shows payment methods as tabs
                        paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'link'],
                        fields: {
                           
                        }
                    }}
                />
            </div>

            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 16 }}>
                🔒 Secured by Stripe · Temporary hold now, charged only when the pickup is completed
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Back</Btn>
                <Btn
                    onClick={handleSubmit}
                    disabled={loading || !ready || !stripe}
                    fullWidth
                    size="lg"
                >
                    {loading ? 'Processing...' : `Authorize $${amount.toFixed(2)}`}
                </Btn>
            </div>

            {testMode && (
                <div style={{ marginTop: 14, padding: 12, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, marginBottom: 4 }}>
                        Test cards
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>✓ Success: 4242 4242 4242 4242</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>✗ Decline: 4000 0000 0000 0002</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>Use any future expiry and any CVC</div>
                </div>
            )}
        </div>
    );
}

// ── Exported wrapper ──────────────────────────────────────────────────────────

interface PaymentFormProps {
    jobId: string;
    amount: number;
    clientSecret: string;
    onSuccess: () => void;
    onCancel: () => void;
}

function makeStripeAppearance(primaryColor: string): StripeElementsOptions['appearance'] {
    return {
        theme: 'stripe',
        variables: {
            colorPrimary: primaryColor,
            colorBackground: '#ffffff',
            colorText: '#111111',
            colorDanger: '#C0392B',
            borderRadius: '8px',
            fontFamily: 'Inter, sans-serif',
            spacingUnit: '4px',
        },
        rules: {
            '.Tab': { border: `1px solid ${C.border}`, borderRadius: '8px' },
            '.Tab--selected': { borderColor: primaryColor, boxShadow: `0 0 0 2px ${primaryColor}22` },
            '.Input': { border: `1px solid ${C.border}`, borderRadius: '8px' },
            '.Input:focus': { borderColor: primaryColor, boxShadow: `0 0 0 2px ${primaryColor}22` },
        },
    };
}

// Shown on every authorize-now / capture-on-completion flow. Customers were surprised to see
// their bank's available balance drop for the hold and read it as a charge — this spells out
// exactly what a card authorization does and doesn't do.
export function AuthorizationHoldNotice({ amount }: { amount: number }) {
    return (
        <div style={{
            background: '#FFF8E6', border: '1px solid #F2D98A', borderRadius: 8,
            padding: '10px 12px', margin: '0 0 18px', fontSize: 12.5, color: '#6b5300', lineHeight: 1.5,
        }}>
            <strong>This is a hold, not a charge.</strong> We place a temporary authorization hold of{' '}
            <strong>${amount.toFixed(2)}</strong> on your card now. Your bank may show it as pending and
            lower your available balance. You're only charged when the pickup is completed — if it's
            cancelled or doesn't happen, the hold is released. Your bank controls how fast a released
            hold clears (often a few days, and slower on debit cards).
        </div>
    );
}

export function PaymentForm({ jobId, amount, clientSecret, onSuccess, onCancel }: PaymentFormProps) {
    const branding = useBranding();
    const stripeAppearance = makeStripeAppearance(branding.buttonColor ?? branding.primaryColor);
    return (
        <Card>
            <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>💳 Payment</h3>
            <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 14px' }}>
                We authorize <strong>${amount.toFixed(2)}</strong> now and charge it only when the
                pickup is completed.
            </p>
            <AuthorizationHoldNotice amount={amount} />
            <Elements stripe={getStripePromise(branding.stripePublishableKey)} options={{ clientSecret, appearance: stripeAppearance }}>
                <CheckoutForm amount={amount} onSuccess={onSuccess} onCancel={onCancel} />
            </Elements>
        </Card>
    );
}

// ── Subscription payment form ─────────────────────────────────────────────────

interface SubscriptionPaymentFormProps {
    clientSecret: string;
    planName: string;
    amount: number;
    onSuccess: () => void;
    onCancel: () => void;
}

function SubscriptionCheckoutForm({ amount, planName, onSuccess, onCancel }: { amount: number; planName: string; onSuccess: () => void; onCancel: () => void }) {
    const stripe = useStripe();
    const elements = useElements();
    const testMode = isTestStripeKey(useBranding().stripePublishableKey);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [ready, setReady] = useState(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) return;
        setError(''); setLoading(true);
        const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: { return_url: window.location.href },
        });
        setLoading(false);
        if (stripeError) { setError(stripeError.message || 'Payment failed'); return; }
        if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'requires_capture') {
            onSuccess();
        }
    };

    return (
        <div>
            <Alert message={error} />
            <div style={{ marginBottom: 20 }}>
                <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'link'] }} />
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 16 }}>🔒 Secured by Stripe · Billed monthly · Cancel anytime</div>
            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Back</Btn>
                <Btn onClick={handleSubmit} disabled={loading || !ready || !stripe} fullWidth size="lg">
                    {loading ? 'Processing…' : `Subscribe — $${amount.toFixed(2)}/mo`}
                </Btn>
            </div>
            {testMode && (
                <div style={{ marginTop: 14, padding: 12, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, marginBottom: 4 }}>Test cards</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>✓ Success: 4242 4242 4242 4242</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>✗ Decline: 4000 0000 0000 0002</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>Use any future expiry and any CVC</div>
                </div>
            )}
        </div>
    );
}

// ── Partial payment milestone form (TREK-80) ────────────────────────────────────

interface MilestonePaymentFormProps {
    clientSecret: string;
    label: string;
    amount: number;
    onSuccess: () => void;
    onCancel: () => void;
}

function MilestoneCheckoutForm({ amount, label, onSuccess, onCancel }: { amount: number; label: string; onSuccess: () => void; onCancel: () => void }) {
    const stripe = useStripe();
    const elements = useElements();
    const testMode = isTestStripeKey(useBranding().stripePublishableKey);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [ready, setReady] = useState(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) return;
        setError(''); setLoading(true);
        // Milestones capture immediately (not authorize-only) — this is the actual charge.
        const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: { return_url: window.location.href },
        });
        setLoading(false);
        if (stripeError) { setError(stripeError.message || 'Payment failed'); return; }
        if (paymentIntent?.status === 'succeeded') onSuccess();
    };

    return (
        <div>
            <Alert message={error} />
            <div style={{ marginBottom: 20 }}>
                <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'link'] }} />
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 16 }}>🔒 Secured by Stripe · Charged immediately for this milestone</div>
            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Back</Btn>
                <Btn onClick={handleSubmit} disabled={loading || !ready || !stripe} fullWidth size="lg">
                    {loading ? 'Processing…' : `Pay $${amount.toFixed(2)} — ${label}`}
                </Btn>
            </div>
            {testMode && (
                <div style={{ marginTop: 14, padding: 12, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, marginBottom: 4 }}>Test cards</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>✓ Success: 4242 4242 4242 4242</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>✗ Decline: 4000 0000 0000 0002</div>
                </div>
            )}
        </div>
    );
}

export function MilestonePaymentForm({ clientSecret, label, amount, onSuccess, onCancel }: MilestonePaymentFormProps) {
    const branding = useBranding();
    const stripeAppearance = makeStripeAppearance(branding.buttonColor ?? branding.primaryColor);
    return (
        <Card>
            <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>💳 {label} Payment</h3>
            <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 20px' }}>
                This milestone — <strong>${amount.toFixed(2)}</strong> — will be charged now.
            </p>
            <Elements stripe={getStripePromise(branding.stripePublishableKey)} options={{ clientSecret, appearance: stripeAppearance }}>
                <MilestoneCheckoutForm amount={amount} label={label} onSuccess={onSuccess} onCancel={onCancel} />
            </Elements>
        </Card>
    );
}

export function SubscriptionPaymentForm({ clientSecret, planName, amount, onSuccess, onCancel }: SubscriptionPaymentFormProps) {
    const branding = useBranding();
    const stripeAppearance = makeStripeAppearance(branding.buttonColor ?? branding.primaryColor);
    return (
        <Card>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>💳 Subscribe to {planName}</h3>
            <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 20px' }}>
                You'll be billed <strong>${amount.toFixed(2)}/month</strong>. Cancel anytime from your profile.
            </p>
            <Elements stripe={getStripePromise(branding.stripePublishableKey)} options={{ clientSecret, appearance: stripeAppearance }}>
                <SubscriptionCheckoutForm amount={amount} planName={planName} onSuccess={onSuccess} onCancel={onCancel} />
            </Elements>
        </Card>
    );
}
